import { Dive } from '../types/Dive';
import { DiveLocation } from '../services/userDataService';

/**
 * Gets the display name for a dive using the following hierarchy:
 *
 * Priority 1: Dive Name (user manually entered, specific to this dive)
 * Priority 2: User preference determines the primary source:
 *   - If "geotagged" (default): Geotagged location → Suunto description (fallback)
 *   - If "description": Suunto description → Geotagged location (fallback)
 * Priority 3: User-edited location overrides participate as the geotagged location source.
 *
 * @param dive - The dive object
 * @param customLocation - Optional custom location from DiveLocation table
 * @param locationDisplayPreference - User's preference for location display ("geotagged" or "description")
 * @returns The display name to show for this dive
 */
export const getDiveDisplayName = (
  dive: Dive,
  customLocation?: DiveLocation,
  locationDisplayPreference: 'geotagged' | 'description' = 'geotagged'
): string => {
  const manualDiveName = dive.diveName?.trim();
  if (manualDiveName) {
    return manualDiveName;
  }

  const geotaggedLocation = customLocation?.locationName?.trim() || dive.location?.trim() || '';
  const suuntoDescription = dive.description?.trim() || '';

  if (locationDisplayPreference === 'description') {
    if (suuntoDescription) {
      return suuntoDescription;
    }
    if (geotaggedLocation) {
      return geotaggedLocation;
    }
    return 'Unknown Location';
  }

  if (geotaggedLocation) {
    return geotaggedLocation;
  }

  if (suuntoDescription) {
    return suuntoDescription;
  }

  return 'Unknown Location';
};

/**
 * Checks whether a saved dive location is an explicit user override.
 * Records created before this flag existed are treated as cached geocodes.
 */
export const isUserEditedLocation = (customLocation?: DiveLocation): boolean => {
  return customLocation?.isUserEdited === true;
};

/**
 * Checks if the display name is showing coordinates (geolocation fallback)
 * This is useful for showing the "Locate" button
 */
export const isCoordinateFormat = (displayName: string): boolean => {
  return Boolean(displayName && displayName.includes('°') && displayName.includes(','));
};
